import React, { useEffect, useState } from 'react'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { tagService } from '../../services/tag.service'
import { Tag as TagType } from '../../types'
import './Tag.scss'

interface TagProps {
  tagID?: number
  tag?: TagType
  clickable?: boolean
  linkTitle?: string
  onClick?: (tagId: number) => void
  className?: string
  showParents?: boolean
}

const Tag: React.FC<TagProps> = ({ tagID, tag: tagProp, clickable = false, linkTitle = '', onClick, className = '', showParents = false }) => {
  const [tag, setTag] = useState<TagType | null>(tagProp || null)
  const [loading, setLoading] = useState(!tagProp)
  const [parentChain, setParentChain] = useState<TagType[]>([])

  useEffect(() => {
    if (tagProp) {
      setTag(tagProp)
      setLoading(false)
      return
    }

    if (!tagID) {
      setLoading(false)
      return
    }
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)) {
      setLoading(false)
      return
    }

    const fetchTag = async () => {
      try {
        const fetchedTag = await tagService.getCached(tagID)
        setTag(fetchedTag)
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }

    fetchTag()
  }, [tagID, tagProp])

  useEffect(() => {
    if (showParents && tag?.parent) {
      const fetchParentChain = async () => {
        try {
          const chain: TagType[] = []
          let current = tag
          const guard = new Set<number>()

          while (current?.parent) {
            if (guard.has(current.parent)) break
            guard.add(current.parent)
            const parent = await tagService.getCached(current.parent)
            if (!parent) break
            chain.unshift(parent)
            current = parent
          }

          setParentChain(chain)
        } catch (error) {}
      }
      fetchParentChain()
    } else {
      setParentChain([])
    }
  }, [showParents, tag])

  if (loading) {
    return <span className="badge bg-secondary">Loading...</span>
  }

  if (!tag) {
    return clickable && tagID ? (
      <button
        type="button"
        className={`badge private ${className}`}
        title={linkTitle}
        onClick={(e) => {
          e.stopPropagation()
          if (onClick && tagID) onClick(tagID)
        }}
        style={{ border: 'none', background: 'inherit' }}
      >
        Private
      </button>
    ) : (
      <span className={`badge private ${className}`}>Private</span>
    )
  }

  const tagStyle = {
    backgroundColor: tag.color,
    color: tag.text_color,
  }

  const renderTag = (tagToRender: TagType, isParent: boolean = false) => {
    if (clickable && onClick && !isParent) {
      return (
        <button
          type="button"
          className={`badge ${className}`}
          title={linkTitle}
          onClick={(e) => {
            e.stopPropagation()
            onClick(tagToRender.id)
          }}
          style={{ ...tagStyle, cursor: 'pointer', border: 'none' }}
        >
          {tagToRender.name}
        </button>
      )
    }

    return (
      <span className={`badge ${className}`} style={tagStyle}>
        {tagToRender.name}
      </span>
    )
  }

  if (showParents && parentChain.length > 0) {
    return (
      <>
        {parentChain.map((parent, index) => (
          <React.Fragment key={parent.id}>
            {renderTag(parent, true)}
            {index < parentChain.length - 1 && <>&nbsp;&gt;&nbsp;</>}
          </React.Fragment>
        ))}
        {parentChain.length > 0 && <>&nbsp;&gt;&nbsp;</>}
        {renderTag(tag)}
      </>
    )
  }

  return renderTag(tag)
}

export default Tag
